<template>
  <div class="sankey-container" :style="{ height: `${height}px` }">
    <svg
      v-if="layout.nodes.length && layout.links.length"
      :viewBox="`0 0 ${width} ${height}`"
      preserveAspectRatio="xMidYMid meet"
      class="sankey-svg"
    >
      <!--
        Die Summe steht an beiden Enden des Flusses, links wie rechts, damit sie
        dort lesbar ist, wo das Auge ohnehin hinsieht. Zweimal derselbe Wert und
        nicht Zufluss gegen Abfluss: in der Maßnahmenansicht ist die Zuflussseite
        keine eigenständige Größe, weil ein Verbraucher mit verknüpftem Erzeuger
        dessen Brennstoff **und** die daraus erzeugte Wärme als Quelle führt —
        eine Summe darüber zählte doppelt.
      -->
      <g v-if="hatSumme" class="sankey-totals">
        <text x="4" y="15" text-anchor="start" class="sankey-total-label">
          {{ summeText }}
        </text>
        <text :x="width - 4" y="15" text-anchor="end" class="sankey-total-label">
          {{ summeText }}
        </text>
      </g>

      <g class="sankey-links">
        <path
          v-for="(link, index) in layout.links"
          :key="`link-${index}`"
          :d="link.path"
          fill="none"
          :stroke="link.color"
          :stroke-opacity="0.45"
          :stroke-width="Math.max(1, link.width || 1)"
        >
          <title>{{ `${link.sourceName} → ${link.targetName}: ${formatNumber(link.value)} ${unit}` }}</title>
        </path>
      </g>

      <g class="sankey-link-values">
        <text
          v-for="(link, index) in layout.links"
          :key="`link-value-${index}`"
          :x="link.labelX"
          :y="link.labelY"
          dy="-2"
          text-anchor="middle"
          class="sankey-link-label"
        >
          {{ `${formatNumber(link.value)} ${unit}` }}
        </text>
      </g>

      <g class="sankey-nodes">
        <g v-for="(node, index) in layout.nodes" :key="`node-${index}`">
          <rect
            :x="node.x0"
            :y="node.y0"
            :width="Math.max(1, node.x1 - node.x0)"
            :height="Math.max(1, node.y1 - node.y0)"
            :fill="node.color"
            :stroke="node.strokeColor"
            stroke-width="1.5"
          >
            <title>{{ `${node.name}: ${formatNumber(node.value)} ${unit}` }}</title>
          </rect>

          <text
            :x="node.x0 < width / 2 ? node.x1 + 6 : node.x0 - 6"
            :y="(node.y0 + node.y1) / 2"
            dy="0.35em"
            :text-anchor="node.x0 < width / 2 ? 'start' : 'end'"
            class="sankey-label"
          >
            {{ node.name }}
          </text>
        </g>
      </g>
    </svg>

    <div v-else class="empty-state">
      Keine Sankey-Daten verfügbar.
    </div>
  </div>
</template>

<script>
import { sankey, sankeyLinkHorizontal } from "d3-sankey";

export default {
  name: "SankeyChart",
  props: {
    nodes: {
      type: Array,
      default: () => [],
    },
    links: {
      type: Array,
      default: () => [],
    },
    width: {
      type: Number,
      default: 1100,
    },
    height: {
      type: Number,
      default: 420,
    },
    /*
     * Die Summe des Diagramms. `null` blendet sie aus; der Wert kommt von
     * aussen, weil nur die Ansicht weiss, was hier zu summieren ist.
     */
    total: {
      type: Number,
      default: null,
    },
    /*
     * Die Einheit stand frueher als `kWh` fest im Markup — auch in den
     * CO2e-Diagrammen, die in kg rechnen. Jede Bandbeschriftung dort war
     * damit falsch beschriftet.
     */
    unit: {
      type: String,
      default: 'kWh',
    },
  },
  data() {
    return {
      layout: {
        nodes: [],
        links: [],
      },
    };
  },
  computed: {
    hatSumme() {
      return Number.isFinite(Number(this.total));
    },
    summeText() {
      return `Summe ${this.formatNumber(this.total)} ${this.unit}`;
    },
    /*
     * Hoehe des Streifens, den die Summenzeile oben belegt. Ohne ihn liefe der
     * oberste Knoten in die Schrift, denn d3-sankey nutzt die volle Flaeche.
     */
    kopfband() {
      return this.hatSumme ? 26 : 1;
    },
  },
  watch: {
    nodes: {
      handler() {
        this.computeLayout();
      },
      deep: true,
      immediate: true,
    },
    links: {
      handler() {
        this.computeLayout();
      },
      deep: true,
      immediate: true,
    },
    width() {
      this.computeLayout();
    },
    height() {
      this.computeLayout();
    },
    total() {
      this.computeLayout();
    },
  },
  methods: {
    normalizeName(name) {
      return String(name || "")
        .toLowerCase()
        .replace(/ä/g, "ae")
        .replace(/ö/g, "oe")
        .replace(/ü/g, "ue")
        .replace(/ß/g, "ss");
    },
    detectEnergyColor(nodeName) {
      const normalized = this.normalizeName(nodeName);

      // Typical engineering colors: electricity yellow, heat red, cooling blue, fuels orange/brown.
      if (/(strom|elektr|netzstrom|power)/.test(normalized)) {
        return "#f9a825";
      }
      if (/(waerme|fernwaerme|heiz|therm|dampf|steam)/.test(normalized)) {
        return "#e53935";
      }
      if (/(kaelte|kuehl|cool|chill)/.test(normalized)) {
        return "#1e88e5";
      }
      if (/(gas|pellet|holz|oel|diesel|benzin|brenn)/.test(normalized)) {
        return "#ef6c00";
      }
      if (/(druckluft|luft)/.test(normalized)) {
        return "#00acc1";
      }
      if (/(wasser|hydro)/.test(normalized)) {
        return "#26a69a";
      }
      if (/(licht|beleucht)/.test(normalized)) {
        return "#ffd54f";
      }
      return null;
    },
    toNodeColor(nodeType, nodeName) {
      const energyColor = this.detectEnergyColor(nodeName);
      if (energyColor) {
        return energyColor;
      }

      if (nodeType === "action") {
        return "#90a4ae";
      }
      if (nodeType === "generator") {
        return "#8e24aa";
      }
      if (nodeType === "ne") {
        return "#43a047";
      }
      if (nodeType === "ne-gen") {
        return "#7cb342";
      }
      /*
       * Verluste. Gedecktes Grau statt des Grüns der Nutzenergie — sie sind
       * ein Ausgang des Maßnahmenknotens, aber kein Ergebnis, und sollen im
       * Bild nicht wie eines aussehen.
       */
      if (nodeType === "loss") {
        return "#b0a9a0";
      }
      return "#607d8b";
    },
    toNodeBorderColor(nodeType) {
      if (nodeType === "action") {
        return "#37474f";
      }
      if (nodeType === "generator") {
        return "#4a148c";
      }
      if (nodeType === "ne") {
        return "#1b5e20";
      }
      if (nodeType === "ne-gen") {
        return "#33691e";
      }
      if (nodeType === "loss") {
        return "#6d6257";
      }
      return "#263238";
    },
    toLinkColor(sourceNode, targetNode) {
      const sourceType = sourceNode?.nodeType || "ee";
      const sourceName = sourceNode?.name || "";
      const targetType = targetNode?.nodeType || "ne";
      const targetName = targetNode?.name || "";

      if (sourceType === "action" || sourceType === "generator") {
        return this.toNodeColor(targetType, targetName);
      }

      return this.toNodeColor(sourceType, sourceName);
    },
    formatNumber(value) {
      const number = Number(value) || 0;
      return new Intl.NumberFormat("de-DE", { maximumFractionDigits: 0 }).format(number);
    },
    computeLayout() {
      if (!Array.isArray(this.nodes) || !Array.isArray(this.links) || !this.nodes.length || !this.links.length) {
        this.layout = { nodes: [], links: [] };
        return;
      }

      const nodeByName = new Map();
      this.nodes.forEach((node) => {
        if (!node || !node.name) {
          return;
        }
        nodeByName.set(node.name, {
          name: node.name,
          nodeType: node.nodeType || "ee",
        });
      });

      const graphNodes = Array.from(nodeByName.values()).map((node) => ({ ...node }));
      const graphLinks = this.links
        .filter((link) => link && link.source && link.target && Number(link.value) > 0)
        .map((link) => ({
          source: link.source,
          target: link.target,
          value: Number(link.value),
        }))
        .filter((link) => nodeByName.has(link.source) && nodeByName.has(link.target));

      if (!graphNodes.length || !graphLinks.length) {
        this.layout = { nodes: [], links: [] };
        return;
      }

      const sankeyGenerator = sankey()
        .nodeId((node) => node.name)
        .nodeWidth(16)
        .nodePadding(16)
        .extent([[1, this.kopfband], [this.width - 1, this.height - 6]]);

      const graph = sankeyGenerator({
        nodes: graphNodes,
        links: graphLinks,
      });

      const makePath = sankeyLinkHorizontal();
      this.layout = {
        nodes: (graph.nodes || []).map((node) => ({
          ...node,
          color: this.toNodeColor(node.nodeType, node.name),
          strokeColor: this.toNodeBorderColor(node.nodeType),
        })),
        links: (graph.links || []).map((link) => ({
          ...link,
          sourceName: link.source?.name || "",
          targetName: link.target?.name || "",
          path: makePath(link),
          color: this.toLinkColor(link.source, link.target),
          labelX: (Number(link.source?.x1) + Number(link.target?.x0)) / 2,
          labelY: (Number(link.y0) + Number(link.y1)) / 2,
        })),
      };
    },
  },
};
</script>

<style scoped>
.sankey-container {
  width: 100%;
  position: relative;
}

.sankey-svg {
  width: 100%;
  height: 100%;
  display: block;
}

/*
 * Die Beschriftungen lagen fest auf `#263238`, einem sehr dunklen Blaugrau. Das
 * SVG hat keinen eigenen Grund, es sitzt auf der Karte darunter — im dunklen
 * Modus also dunkle Schrift auf dunklem Grund.
 *
 * Der weisse Rand um die Kantenbeschriftung (`stroke`) ist kein Schmuck,
 * sondern haelt die Schrift ueber den farbigen Baendern lesbar. Er muss die
 * Hintergrundfarbe tragen, nicht Weiss, sonst wird aus dem Trennsaum im
 * dunklen Modus ein leuchtender Rahmen.
 */
.sankey-total-label {
  font-size: 15px;
  font-weight: 700;
  fill: rgb(var(--v-theme-on-surface));
}

.sankey-label {
  font-size: 16px;
  font-weight: 500;
  fill: rgb(var(--v-theme-on-surface));
}

.sankey-link-label {
  font-size: 12px;
  font-weight: 500;
  fill: rgb(var(--v-theme-on-surface));
  paint-order: stroke;
  stroke: rgb(var(--v-theme-surface));
  stroke-width: 3px;
  stroke-linejoin: round;
}

.empty-state {
  height: 100%;
  min-height: 260px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(var(--v-theme-on-surface), 0.6);
}
</style>
